import { MouseEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import DOMPurify from 'dompurify';
import ResourceCard from '../resources/ResourceCard';
import ResourceCardSkeleton from '../resources/ResourceCardSkeleton';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import Input from '../ui/Input';
import { ProgramHub } from '../../types/program';
import { HubResource } from '../../types/hub';
import { WPMedia, WPPost } from '../../types/wordpress';
import { decodeHtmlEntities } from '../../utils/htmlEntities';
import { hubProseClassName } from '../../utils/hubProse';
import { resourceWord } from '../../utils/cardFacts';
import {
  hubOutline,
  hubOutlineTree,
  HubOutlineEntry,
  orderedByOrder,
  programHubHref,
} from '../../utils/hubChapter';
import ToolkitSectionTree from '../hubs/ToolkitSectionTree';
import { sectionAnchors } from '../../utils/headingToc';
import { openPlace, PlaceRoom } from '../../utils/placeMotion';

function ResourceList({
  resources,
  resourcePosts,
  attachmentsByPostId,
  failedPostIds,
  postsReady,
  hubSlug,
  room,
  returnToProgram = false,
  summarizeAttachments = false,
}: {
  resources: HubResource[];
  resourcePosts: Map<number, WPPost>;
  attachmentsByPostId: Map<number, WPMedia[]>;
  failedPostIds: Set<number>;
  postsReady: boolean;
  hubSlug: string;
  room?: PlaceRoom;
  returnToProgram?: boolean;
  summarizeAttachments?: boolean;
}) {
  if (resources.length === 0) return null;

  return (
    <div className="space-y-4">
      {resources.map((resource) => {
        const post = resourcePosts.get(resource.wpPostId);
        if (post) {
          return (
            <ResourceCard
              key={resource.id}
              post={post}
              view="list"
              fromHubSlug={hubSlug}
              attachments={attachmentsByPostId.get(post.id) ?? []}
              room={room}
              returnToProgram={returnToProgram}
              summarizeAttachments={summarizeAttachments}
            />
          );
        }
        if (failedPostIds.has(resource.wpPostId) || postsReady) return null;
        return <ResourceCardSkeleton key={resource.id} />;
      })}
    </div>
  );
}

function SectionBody({ entry }: { entry: HubOutlineEntry }) {
  const section = entry.section;
  if (!section) return null;
  return (
    <>
      {section.type === 'CONTENT' && section.body ? (
        <div
          className={`mb-3 ${hubProseClassName} [&>*:first-child]:mt-0 [&>*:last-child]:mb-0`}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(section.body, { ADD_ATTR: ['target'] }),
          }}
        />
      ) : null}
      {section.type === 'RESOURCE_GROUP' && section.description ? (
        <p className="text-gray-600 mb-5 max-w-3xl">{section.description}</p>
      ) : null}
    </>
  );
}

function matchesQuery(resource: HubResource, resourcePosts: Map<number, WPPost>, query: string) {
  if (!query) return true;
  const post = resourcePosts.get(resource.wpPostId);
  if (!post) return false;
  return decodeHtmlEntities(post.title.rendered).toLowerCase().includes(query);
}

interface ProgramHubChapterProps {
  hub: ProgramHub;
  entered: boolean;
  resourcePosts: Map<number, WPPost>;
  attachmentsByPostId: Map<number, WPMedia[]>;
  failedPostIds: Set<number>;
  postsReady: boolean;
  sectionIdPrefix?: string;
  room?: PlaceRoom;
  programSlug: string;
  titleQuery?: string;
}

export default function ProgramHubChapter({
  hub,
  entered,
  resourcePosts,
  attachmentsByPostId,
  failedPostIds,
  postsReady,
  sectionIdPrefix = 'hub-section-',
  room,
  programSlug,
  titleQuery = '',
}: ProgramHubChapterProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const outline = hubOutline(hub.sections, hub.resources);
  const toolkitTo = programHubHref(programSlug, hub.slug);
  const orderedSections = [...hub.sections].sort(
    (a, b) => a.order - b.order || a.title.localeCompare(b.title)
  );
  const sectionAnchor = sectionAnchors(orderedSections);
  const topSections = orderedSections.filter((section) => !section.title.includes(' / '));
  const titleFilter = titleQuery.trim().toLowerCase();
  const hubTitleMatches = titleFilter.length > 0 && hub.title.toLowerCase().includes(titleFilter);
  const listedSections = !titleFilter || hubTitleMatches
    ? topSections
    : topSections.filter((section) => section.title.toLowerCase().includes(titleFilter));
  const sectionCount = (title: string) => {
    const ids = new Set(
      orderedSections
        .filter((section) => section.title === title || section.title.startsWith(`${title} / `))
        .map((section) => section.id)
    );
    return hub.resources.filter((resource) => resource.sectionId && ids.has(resource.sectionId)).length;
  };
  const openToolkit = (event: MouseEvent<HTMLAnchorElement>, to: string) => {
    openPlace(event, event.currentTarget, { kind: 'hub', slug: hub.slug }, to, navigate, location);
  };
  const query = entered ? (searchParams.get('title') ?? '') : '';
  const normalizedQuery = query.trim().toLowerCase();
  const flatResources = orderedByOrder(hub.resources).filter((resource) =>
    matchesQuery(resource, resourcePosts, normalizedQuery)
  );
  const visibleOutline = outline
    .map((entry) => ({
      ...entry,
      resources: entry.resources.filter((resource) => matchesQuery(resource, resourcePosts, normalizedQuery)),
    }))
    .filter((entry) => !normalizedQuery || entry.resources.length > 0);
  const missingCount = entered
    ? hub.resources.filter((resource) => failedPostIds.has(resource.wpPostId)).length
    : 0;
  const showFilter = entered && hub.resources.length > 12;
  const Heading = entered ? 'h1' : 'h2';

  return (
    <section id={`hub-${hub.slug}`} className={entered ? undefined : 'scroll-mt-24'}>
      <div className="flex items-start justify-between gap-4 mb-3">
        {entered ? (
          <Heading
            id="entered-hub-title"
            tabIndex={-1}
            className="text-2xl md:text-3xl font-bold text-gray-900 focus-ring rounded-sm"
          >
            {hub.title}
          </Heading>
        ) : (
          <h2 tabIndex={-1} className="text-2xl font-semibold text-gray-900 focus-ring rounded-sm">
            <Link
              to={toolkitTo}
              className="hover:text-primary transition-colors focus-ring rounded-sm"
              onClick={(event) => openToolkit(event, toolkitTo)}
            >
              {hub.title}
            </Link>
          </h2>
        )}
        <SaveToMyOrbconButton
          type="HUB"
          hubId={hub.id}
          title={hub.title}
          slug={hub.slug}
          variant="detail"
        />
      </div>
      {hub.description ? (
        <p className="text-lg text-gray-600 max-w-3xl mb-5">{hub.description}</p>
      ) : null}
      {postsReady && missingCount > 0 ? (
        <p className="text-sm text-gray-600 mb-4">
          {missingCount === 1
            ? '1 document could not be loaded.'
            : `${missingCount} documents could not be loaded.`}
        </p>
      ) : null}
      {showFilter ? (
        <div className="relative max-w-xl mb-8">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <Input
            type="search"
            placeholder="Filter resources by title"
            value={query}
            onChange={(event) => {
              const next = new URLSearchParams(searchParams);
              if (event.target.value) next.set('title', event.target.value);
              else next.delete('title');
              const search = next.toString();
              navigate(
                {
                  pathname: location.pathname,
                  search: search ? `?${search}` : '',
                  hash: location.hash,
                },
                { replace: true, preventScrollReset: true, state: location.state }
              );
            }}
            className="pl-10"
            aria-label="Filter resources by title"
          />
        </div>
      ) : null}
      {entered && outline.length > 0 ? (
        <>
          <ToolkitSectionTree
            nodes={hubOutlineTree(visibleOutline)}
            filtering={Boolean(normalizedQuery)}
            idFor={(node) => `${sectionIdPrefix}${node.id}`}
            renderEntry={(node) => {
              const entry = node.entry;
              if (!entry) return null;
              return (
                <>
                  <SectionBody entry={entry} />
                  <ResourceList
                    resources={entry.resources}
                    resourcePosts={resourcePosts}
                    attachmentsByPostId={attachmentsByPostId}
                    failedPostIds={failedPostIds}
                    postsReady={postsReady}
                    hubSlug={hub.slug}
                    room={room}
                  />
                  {entry.resources.length === 0 &&
                  node.children.length === 0 &&
                  entry.section?.type !== 'CONTENT' ? (
                    <p className="text-gray-500">No resources in this section yet.</p>
                  ) : null}
                </>
              );
            }}
          />
          {normalizedQuery && visibleOutline.length === 0 ? (
            <p className="text-gray-600">No resources match that title.</p>
          ) : null}
        </>
      ) : entered ? (
        <ResourceList
          resources={
            normalizedQuery
              ? flatResources
              : orderedByOrder(hub.resources)
          }
          resourcePosts={resourcePosts}
          attachmentsByPostId={attachmentsByPostId}
          failedPostIds={failedPostIds}
          postsReady={postsReady}
          hubSlug={hub.slug}
          room={room}
        />
      ) : (
        <>
          {listedSections.length > 0 && (
            <ul className="mt-1">
              {listedSections.map((section) => (
                <li key={section.id}>
                  <Link
                    to={`${toolkitTo}#${sectionAnchor.get(section.id)}`}
                    className="flex min-h-[44px] items-center text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
                    onClick={(event) => openToolkit(event, `${toolkitTo}#${sectionAnchor.get(section.id)}`)}
                  >
                    <span className="min-w-0">{section.title}</span>
                    <span className="ml-auto pl-3 text-gray-500">{resourceWord(sectionCount(section.title))}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link
            to={toolkitTo}
            className="inline-flex min-h-[44px] items-center text-sm font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
            onClick={(event) => openToolkit(event, toolkitTo)}
          >
            Open toolkit
          </Link>
        </>
      )}
    </section>
  );
}
